// src/components/mkh/Question.js
import React, { useState, useRef, useEffect } from "react";
import { FaPause, FaPlay, FaListUl } from "react-icons/fa";
import {
  FaClockRotateLeft,
  FaMoneyBillTransfer,
  FaXmark,
  FaForward,
  FaBackward,
  FaChevronDown,
  FaChevronUp,
} from "react-icons/fa6";
import { HiPhone } from "react-icons/hi2";

import ReactPlayer from "react-player";
import { useStopwatch, useTimer } from "react-timer-hook";
import textSizeBasedOnLength from "../../funcs/textResizer";
import { encryptData } from "../../funcs/encryption";
import { truncateText } from "../../utils/textUtils";
import {
  getAnswerStats,
  getFirstMeaningfulLetter,
} from "../../utils/arabicTextUtils";
import TextModal from "../modals/TextModal";
import NoWrongAnswersModal from "../modals/NoWrongAnswersModal";
import DrawBoard from "../../draw/DrawBoard";

const MKHQuestion = ({ game, ads, question, setShowAnswer }) => {
  const [fullScreen, setFullScreen] = useState(false);
  const [selectedPowerUp, setSelectedPowerUp] = useState({ name: null });
  const [showMultipleChoice, setShowMultipleChoice] = useState(false);
  const [multipleChoiceAnswers, setMultipleChoiceAnswers] = useState([]);
  const [isChoicesMinimized, setIsChoicesMinimized] = useState(false);
  const [showTextModal, setShowTextModal] = useState(false);
  const [showNoWrongAnswersModal, setShowNoWrongAnswersModal] = useState(false);
  const [alternativePowerUpUsed, setAlternativePowerUpUsed] = useState(false);
  const [selectedTeamForModal, setSelectedTeamForModal] = useState(null);
  const [showFirstLetterHint, setShowFirstLetterHint] = useState(false);
  const [firstLetterHint, setFirstLetterHint] = useState("");
  const [isFirstLetterMinimized, setIsFirstLetterMinimized] = useState(false);
  const [showAnswerPattern, setShowAnswerPattern] = useState(false);
  const [answerPatternInfo, setAnswerPatternInfo] = useState(null);
  const [isAnswerPatternMinimized, setIsAnswerPatternMinimized] =
    useState(false);
  const { seconds, minutes, isRunning, start, pause, reset } = useStopwatch({
    autoStart: true,
  });
  const callTimer = useTimer({
    expiryTimestamp: Date.now() + 30000,
    autoStart: false,
  });

  // Audio player state
  const audioRef = useRef(null);
  const [isAudioPlaying, setIsAudioPlaying] = useState(false);
  const [audioDuration, setAudioDuration] = useState(0);
  const [audioPosition, setAudioPosition] = useState(0);
  // eslint-disable-next-line
  const [audioLoaded, setAudioLoaded] = useState(false);
  const [answers, setAnswers] = useState([]);

  const modeName = game?.modes?.find((mode) =>
    mode.questions.find(
      (q) => (q?.question?._id || q?.customQuestion?._id) === question?._id
    )
  )?.name;

  const hasWrongAnswers = !!(
    question?.wrongAnswers && question?.wrongAnswers.length > 0
  );

  const handleFinishPowerUp = () => {
    const team = game.teams.find((t) => t._id === selectedPowerUp.team._id);
    const powerUp = team.powerUps.find((p) => p.name === selectedPowerUp.name);
    if (powerUp) {
      powerUp.used = true;
      localStorage.setItem("game", encryptData(game));
      setSelectedPowerUp({ name: null });
    }
  };

  const handleUseShowFirstLetter = () => {
    if (selectedTeamForModal) {
      // Create and show first letter hint - just the first letter
      const answer = question?.answer || question?.customQuestion?.answer || "";
      const { firstLetter } = getFirstMeaningfulLetter(answer);
      setFirstLetterHint(firstLetter);
      setShowFirstLetterHint(true);

      // Mark multiple choice powerup as used (not the other powerups)
      const team = game.teams.find((t) => t._id === selectedTeamForModal._id);
      const powerUp = team.powerUps.find(
        (p) => p.name !== "one" && p.name !== "two"
      );
      if (powerUp) {
        powerUp.used = true;
        localStorage.setItem("game", encryptData(game));
      }

      setAlternativePowerUpUsed(true);
      setShowNoWrongAnswersModal(false);
      setSelectedTeamForModal(null);
    }
  };

  const handleUseShowAnswerPattern = () => {
    if (selectedTeamForModal) {
      // Create and show answer pattern
      const answer = question?.answer || question?.customQuestion?.answer || "";
      const stats = getAnswerStats(answer);
      setAnswerPatternInfo(stats);
      setShowAnswerPattern(true);

      // Mark multiple choice powerup as used (not the other powerups)
      const team = game.teams.find((t) => t._id === selectedTeamForModal._id);
      const powerUp = team.powerUps.find(
        (p) => p.name !== "one" && p.name !== "two"
      );
      if (powerUp) {
        powerUp.used = true;
        localStorage.setItem("game", encryptData(game));
      }

      setAlternativePowerUpUsed(true);
      setShowNoWrongAnswersModal(false);
      setSelectedTeamForModal(null);
    }
  };

  // Audio player functions
  const handleAudioPlayPause = () => {
    setIsAudioPlaying(!isAudioPlaying);
  };

  const handleAudioSkipForward = () => {
    if (audioRef.current) {
      const currentTime = audioRef.current.getCurrentTime();
      audioRef.current.seekTo(currentTime + 15);
    }
  };

  const handleAudioSkipBackward = () => {
    if (audioRef.current) {
      const currentTime = audioRef.current.getCurrentTime();
      audioRef.current.seekTo(Math.max(0, currentTime - 15));
    }
  };

  const handleAudioProgress = (state) => {
    setAudioPosition(state.playedSeconds);
  };

  const handleAudioDuration = (duration) => {
    setAudioDuration(duration);
    setAudioLoaded(true);
  };

  const handleAudioSeek = (e) => {
    const seekValue = parseFloat(e.target.value);
    if (audioRef.current) {
      audioRef.current.seekTo(seekValue);
    }
    setAudioPosition(seekValue);
  };

  const formatTime = (timeInSeconds) => {
    if (isNaN(timeInSeconds)) return "0:00";
    const minutes = Math.floor(timeInSeconds / 60);
    const seconds = Math.floor(timeInSeconds % 60);
    return `${minutes}:${seconds < 10 ? "0" : ""}${seconds}`;
  };

  useEffect(() => {
    if (hasWrongAnswers) {
      setAnswers(
        [...question?.wrongAnswers, question?.answer].sort(
          () => Math.random() - 0.5
        )
      );
    }
  }, [question, hasWrongAnswers]);

  useEffect(() => {
    if (showMultipleChoice && question?.wrongAnswers) {
      setMultipleChoiceAnswers(
        [...question.wrongAnswers, question.answer].sort(
          () => Math.random() - 0.5
        )
      );
    }
  }, [showMultipleChoice, question]);

  const questionText = question?.question || "";
  const { truncated: truncatedQuestion, needsTruncation } = truncateText(
    questionText,
    120
  );

  return (
    <div className="w-full h-[90vh] bg-[#eaf6fb] flex flex-col">
      {/* Header Card - Thinner */}
      <div className="p-2 flex-shrink-0">
        <div className="bg-white rounded-xl p-3 shadow-lg border-2 border-[#bfe4f3]">
          <div className="flex justify-between items-center">
            {/* Left - Mode Info */}
            <div className="flex items-center space-x-3">
              <div className="bg-[#29a6d6] rounded-lg px-3 py-1">
                <h2 className="text-white font-bold text-sm">{modeName}</h2>
              </div>
              <div className="bg-[#bfe4f3] rounded-lg px-3 py-1">
                <h2 className="text-[#29a6d6] font-bold text-sm">
                  {question?.points} نقطة
                </h2>
              </div>
              {question?.custom && (
                <div className="bg-[#eaf6fb] border-2 border-[#29a6d6] rounded-lg px-2 py-1">
                  <span className="text-[#29a6d6] font-semibold text-xs">
                    فئة خاصة
                  </span>
                </div>
              )}
            </div>

            {/* Center - Timer */}
            <div className="bg-[#29a6d6] rounded-xl p-2 shadow-lg">
              <div className="flex items-center space-x-4 text-white">
                <button onClick={() => (isRunning ? pause() : start())}>
                  {isRunning ? <FaPause size={16} /> : <FaPlay size={16} />}
                </button>
                <div className="text-lg font-mono font-bold">
                  {minutes.toString().padStart(2, "0")}:
                  {seconds.toString().padStart(2, "0")}
                </div>
                <button onClick={reset}>
                  <FaClockRotateLeft size={16} />
                </button>
              </div>
            </div>

            {/* Right - Controls */}
            <div className="flex items-center space-x-3">
              <button
                onClick={() => setShowAnswer(true)}
                className="bg-[#29a6d6] hover:bg-[#1e7ba6] text-white px-4 py-2 rounded-lg font-bold transition-colors text-sm"
              >
                إظهار الإجابة
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className=" flex p-2 gap-2 flex-1 min-h-0">
        {/* Question Content Card */}
        <div className="relative flex-1 min-w-0">
          <div className="bg-white rounded-xl p-4 shadow-lg border-2 border-[#bfe4f3] h-full flex flex-col">
            {/* Question Text */}
            <div className="mb-4 flex-shrink-0">
              <div className="bg-[#eaf6fb] rounded-lg p-4 border border-[#bfe4f3]">
                <h1
                  className="text-[#29a6d6] font-bold text-center leading-relaxed"
                  style={{
                    fontSize: textSizeBasedOnLength(questionText),
                    direction: "rtl",
                  }}
                  onClick={() => needsTruncation && setShowTextModal(true)}
                >
                  {needsTruncation ? truncatedQuestion : questionText}
                </h1>
                {needsTruncation && (
                  <button
                    onClick={() => setShowTextModal(true)}
                    className="mt-2 px-3 py-1 bg-[#29a6d6] hover:bg-[#1e7ba6] text-white rounded-lg text-xs transition-colors"
                  >
                    اقرأ المزيد
                  </button>
                )}
              </div>
            </div>

            {/* Media Content */}
            <div className="flex-1 min-h-0">
              {/* Video */}
              {question?.questionVideo &&
                question?.questionVideo.length > 10 && (
                  <div className="rounded-lg overflow-hidden border-2 border-[#bfe4f3] h-full">
                    <ReactPlayer
                      url={question?.questionVideo}
                      controls
                      width="100%"
                      height="100%"
                    />
                  </div>
                )}

              {/* Audio */}
              {question?.questionAudio &&
                question?.questionAudio.length > 10 && (
                  <div className="bg-[#eaf6fb] rounded-lg p-4 border border-[#bfe4f3] h-full flex flex-col justify-center">
                    <h3 className="text-[#29a6d6] font-bold mb-3 text-center text-sm">
                      ملف صوتي
                    </h3>
                    <ReactPlayer
                      ref={audioRef}
                      url={question?.questionAudio}
                      playing={isAudioPlaying}
                      onProgress={handleAudioProgress}
                      onDuration={handleAudioDuration}
                      width="0"
                      height="0"
                    />
                    <div className="flex items-center space-x-3">
                      <button
                        onClick={handleAudioPlayPause}
                        className="p-2 bg-[#29a6d6] text-white rounded-full hover:bg-[#1e7ba6]"
                      >
                        {isAudioPlaying ? (
                          <FaPause size={12} />
                        ) : (
                          <FaPlay size={12} />
                        )}
                      </button>
                      <button
                        onClick={handleAudioSkipBackward}
                        className="p-1 bg-[#bfe4f3] text-[#29a6d6] rounded hover:bg-[#a8d4e8]"
                      >
                        <FaBackward size={10} />
                      </button>
                      <input
                        type="range"
                        min="0"
                        max={audioDuration}
                        value={audioPosition}
                        onChange={handleAudioSeek}
                        className="flex-1 h-1 bg-[#bfe4f3] rounded appearance-none"
                        style={{
                          background: `linear-gradient(to right, #29a6d6 0%, #29a6d6 ${
                            (audioPosition / audioDuration) * 100
                          }%, #bfe4f3 ${
                            (audioPosition / audioDuration) * 100
                          }%, #bfe4f3 100%)`,
                        }}
                      />
                      <button
                        onClick={handleAudioSkipForward}
                        className="p-1 bg-[#bfe4f3] text-[#29a6d6] rounded hover:bg-[#a8d4e8]"
                      >
                        <FaForward size={10} />
                      </button>
                      <span className="text-[#29a6d6] text-xs font-semibold">
                        {formatTime(Math.floor(audioPosition))} /{" "}
                        {formatTime(Math.floor(audioDuration))}
                      </span>
                    </div>
                  </div>
                )}

              {/* Image */}
              {question?.questionImage && (
                <div className="rounded-lg overflow-hidden border-2 border-[#bfe4f3] h-full">
                  {question?.drawQrCodeId ? (
                    <DrawBoard
                      qrCodeId={question.drawQrCodeId}
                      fallbackImage={question?.questionImage}
                      alt="السؤال"
                    />
                  ) : (
                    <button
                      onClick={() => setFullScreen(true)}
                      className="w-full h-full"
                    >
                      <img
                        src={question?.questionImage}
                        alt="السؤال"
                        className="w-full h-full object-contain"
                      />
                    </button>
                  )}
                </div>
              )}

              {/* Multiple Choice */}
              {hasWrongAnswers && (
                <div className="bg-[#eaf6fb] rounded-lg p-3 border border-[#bfe4f3] h-full flex flex-col">
                  <h3 className="text-[#29a6d6] font-bold mb-3 text-center text-sm">
                    الخيارات
                  </h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-2 flex-1">
                    {answers.map((option, index) => (
                      <div
                        key={index}
                        className="bg-white border-2 border-[#bfe4f3] rounded-lg p-3 text-center text-[#29a6d6] font-semibold hover:bg-[#f8fafc] transition-colors text-sm"
                      >
                        {option}
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Multiple Choice Options */}
              {showMultipleChoice && question?.wrongAnswers && (
                <div
                  className={`absolute ${
                    isChoicesMinimized
                      ? "bottom-4 right-4"
                      : "bottom-4 left-1/2 transform -translate-x-1/2"
                  } transition-all duration-300 z-10`}
                >
                  <div
                    className={`bg-white rounded-xl shadow-2xl border-3 border-[#29a6d6] ${
                      isChoicesMinimized ? "w-auto" : "w-[90vw] max-w-md"
                    }`}
                  >
                    <div className="bg-[#29a6d6] p-2 rounded-t-lg flex justify-between items-center">
                      <button
                        onClick={() =>
                          setIsChoicesMinimized(!isChoicesMinimized)
                        }
                        className="text-white hover:bg-[#ffffff20] rounded-full p-1 transition-colors"
                      >
                        {isChoicesMinimized ? (
                          <FaChevronUp size={16} />
                        ) : (
                          <FaChevronDown size={16} />
                        )}
                      </button>
                      <h3
                        className="text-white font-bold text-sm flex-1 text-center"
                        style={{ direction: "rtl" }}
                      >
                        خيارات متعددة
                      </h3>
                      <div className="w-6" />
                    </div>

                    {!isChoicesMinimized && (
                      <div className="p-3">
                        <p
                          className="text-center text-[#29a6d6] text-xs mb-3"
                          style={{ direction: "rtl" }}
                        >
                          الإجابة الصحيحة موجودة بين هذه الخيارات
                        </p>
                        <div
                          className="grid grid-cols-2 gap-2"
                          style={{ direction: "rtl" }}
                        >
                          {multipleChoiceAnswers.map((option, index) => (
                            <div
                              key={index}
                              className="bg-[#eaf6fb] p-3 rounded-lg text-center font-semibold text-[#29a6d6] border border-[#bfe4f3] relative overflow-hidden text-sm"
                            >
                              <div className="absolute top-1 left-1 w-6 h-6 bg-[#29a6d6] rounded-full flex items-center justify-center text-white text-xs font-bold">
                                {String.fromCharCode(65 + index)}
                              </div>
                              <div className="mt-1">{option}</div>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Teams Sidebar */}
        <div className="w-60 flex-shrink-0">
          <div className="bg-white rounded-xl p-3 shadow-lg border-2 border-[#bfe4f3] h-full flex flex-col">
            <h3 className="text-[#29a6d6] font-bold mb-3 text-center text-sm flex-shrink-0">
              الفرق
            </h3>
            <div className="flex-1 flex flex-col h-full">
              {/* Teams Section - 1/4 of main height */}
              <div className="flex flex-col gap-2 h-full">
                {game?.teams?.map((team, index) => (
                  <div
                    key={index}
                    className={`bg-[#eaf6fb] rounded-lg p-2 border-2 flex-shrink-0 h-[24%] flex justify-between items-center ${
                      team.currentTurn ? "border-[#29a6d6]" : "border-[#bfe4f3]"
                    }`}
                  >
                    <div className="flex items-center justify-between mb-1 w-1/2">
                      <div className="flex items-center space-x-2">
                        <div className="w-6 h-6 rounded-full overflow-hidden border border-[#bfe4f3]">
                          <img
                            src={team?.image?.image}
                            alt={team?.name}
                            className="w-full h-full object-cover"
                          />
                        </div>
                        <div>
                          <h4 className="font-bold text-[#29a6d6] text-xs">
                            {team.name}
                          </h4>
                          <p className="text-[#29a6d6] text-xs">
                            {team.points} نقطة
                          </p>
                        </div>
                      </div>
                    </div>

                    {/* Power-ups */}
                    <div className="flex space-x-1 justify-center w-1/2">
                      {team?.powerUps?.map((powerUp, powerUpIndex) => (
                        <button
                          key={powerUpIndex}
                          disabled={
                            powerUp.used ||
                            (!team.currentTurn && powerUp?.name === "one") ||
                            (powerUp?.name !== "one" &&
                              powerUp?.name !== "two" &&
                              !hasWrongAnswers &&
                              alternativePowerUpUsed)
                          }
                          onClick={() => {
                            if (!powerUp.used) {
                              // Check if it's multiple choice powerup and no wrong answers
                              if (
                                powerUp?.name !== "one" &&
                                powerUp?.name !== "two" &&
                                !hasWrongAnswers
                              ) {
                                setSelectedTeamForModal(team);
                                setShowNoWrongAnswersModal(true);
                              } else {
                                setSelectedPowerUp({
                                  name: powerUp?.name,
                                  team,
                                });
                              }
                            }
                          }}
                          className={`p-2 rounded text-s transition-colors ${
                            powerUp.used
                              ? "bg-gray-300 text-gray-500 cursor-not-allowed"
                              : "bg-[#29a6d6] text-white hover:bg-[#1e7ba6]"
                          }`}
                        >
                          {powerUp?.name === "one" ? (
                            <FaMoneyBillTransfer size={10} />
                          ) : powerUp?.name === "two" ? (
                            <HiPhone size={10} />
                          ) : (
                            <FaListUl size={10} />
                          )}
                        </button>
                      ))}
                    </div>
                  </div>
                ))}
              </div>

              {/* Ads Section - Takes remaining space */}
              {ads && Array.isArray(ads) && ads.length > 0 && (
                <div className="flex-1 min-h-0">
                  <h4 className="text-[#29a6d6] font-bold mb-2 text-center text-xs">
                    الإعلانات
                  </h4>
                  <div className="space-y-1 h-full overflow-hidden">
                    {ads.slice(0, 4).map((ad, index) => (
                      <div
                        key={index}
                        className="rounded-lg overflow-hidden border border-[#bfe4f3]"
                        style={{
                          height: `calc((100% - ${
                            (Math.min(ads.length, 4) - 1) * 0.25
                          }rem) / ${Math.min(ads.length, 4)})`,
                        }}
                      >
                        <img
                          src={ad.image}
                          alt={ad.name}
                          className="w-full h-3/4 object-cover"
                        />
                        <div className="p-1 bg-[#eaf6fb] h-1/4 flex items-center justify-center">
                          <p className="text-[#29a6d6] text-xs font-semibold text-center truncate">
                            {ad.name}
                          </p>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Power-up Modal */}
      {selectedPowerUp.name && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-2xl p-6 m-4 max-w-md w-full border-4 border-[#bfe4f3]">
            <div className="flex justify-between items-center mb-4">
              <h2 className="text-[#29a6d6] font-bold text-xl">
                {selectedPowerUp.name === "one"
                  ? "ما عجبني بغيّره"
                  : selectedPowerUp.name === "two"
                  ? "اتصال بصديق"
                  : "خيارات متعددة"}
              </h2>
              <button
                onClick={() => setSelectedPowerUp({ name: null })}
                className="text-[#29a6d6] hover:text-[#1e7ba6]"
              >
                <FaXmark size={20} />
              </button>
            </div>

            <div className="text-center mb-6">
              <div className="flex items-center justify-center space-x-3 mb-4">
                <img
                  src={selectedPowerUp?.team?.image?.image}
                  alt={selectedPowerUp?.team?.name}
                  className="w-12 h-12 rounded-full border-2 border-[#bfe4f3]"
                />
                <span className="text-[#29a6d6] font-bold">
                  {selectedPowerUp?.team?.name}
                </span>
              </div>

              <p className="text-[#29a6d6] mb-4">
                {selectedPowerUp.name === "one"
                  ? "تقدر تغيّر السؤال حق سؤال عشوائي ثاني بس ها! اذا جاوبته صح بتاخذ نقاط السؤال اليديد"
                  : selectedPowerUp.name === "two"
                  ? "أول ما يرد الي داقين عليه تبلشون التايمر"
                  : "لما تستعملها بتطلع لك خيارات متعددة فيها الإجابة الصحيحة"}
              </p>

              {selectedPowerUp.name === "two" && (
                <div className="mb-4">
                  <button
                    className="w-32 h-32 border-4 border-[#bfe4f3] rounded-full bg-[#eaf6fb] font-semibold text-[#29a6d6]"
                    onClick={() => callTimer.resume()}
                  >
                    {callTimer.totalSeconds > 0 && callTimer.isRunning ? (
                      <span className="text-2xl">{callTimer.totalSeconds}</span>
                    ) : callTimer.totalSeconds === 0 ? (
                      "انتهى الوقت"
                    ) : (
                      <div className="flex flex-col items-center">
                        <HiPhone size={24} />
                        <span className="text-sm">ابدأ المكالمة</span>
                      </div>
                    )}
                  </button>
                </div>
              )}
            </div>

            <button
              onClick={() => {
                if (selectedPowerUp.name === "one") {
                  // Question replacement logic
                  const randomQuestion =
                    game.spareQuestions[
                      Math.floor(Math.random() * game.spareQuestions.length)
                    ];
                  game.spareQuestions = game.spareQuestions.filter(
                    (q) => q._id !== randomQuestion._id
                  );
                  const mode = game.modes.find((mode) =>
                    mode.questions.find(
                      (q) =>
                        (q?.question?._id || q?.customQuestion?._id) ===
                        question?._id
                    )
                  );
                  const qIndex = mode.questions.findIndex(
                    (q) =>
                      (q?.question?._id || q?.customQuestion?._id) ===
                      question?._id
                  );
                  if (qIndex !== -1) {
                    mode.questions[qIndex].question = {
                      ...randomQuestion,
                      spare: true,
                    };
                  }
                  localStorage.setItem("game", encryptData(game));
                  setSelectedPowerUp({ name: null });
                  handleFinishPowerUp();
                  if (!hasWrongAnswers) setAlternativePowerUpUsed(true);
                  window.location.reload();
                } else if (selectedPowerUp.name === "two") {
                  callTimer.restart(new Date(Date.now() + 30000));
                  callTimer.pause();
                  handleFinishPowerUp();
                  if (!hasWrongAnswers) setAlternativePowerUpUsed(true);
                } else {
                  setShowMultipleChoice(true);
                  handleFinishPowerUp();
                }
              }}
              className="w-full bg-[#29a6d6] hover:bg-[#1e7ba6] text-white py-3 rounded-xl font-bold transition-colors"
            >
              {selectedPowerUp.name === "one"
                ? "غيّر السؤال"
                : selectedPowerUp.name === "two"
                ? "خلصت المكالمة"
                : "إظهار الخيارات"}
            </button>
          </div>
        </div>
      )}

      {/* Full Screen Image Modal */}
      {fullScreen && (
        <div
          onClick={() => setFullScreen(false)}
          className="fixed inset-0 bg-black bg-opacity-90 flex items-center justify-center z-50"
        >
          <button
            onClick={() => setFullScreen(false)}
            className="absolute top-4 right-4 text-white hover:text-gray-300"
          >
            <FaXmark size={30} />
          </button>
          <img
            src={question?.questionImage}
            alt="السؤال"
            className="max-w-full h-full object-contain"
          />
        </div>
      )}

      {/* Text Modal */}
      <TextModal
        isOpen={showTextModal}
        onClose={() => setShowTextModal(false)}
        text={questionText}
        title="نص السؤال"
      />

      {/* No Wrong Answers Modal */}
      <NoWrongAnswersModal
        isOpen={showNoWrongAnswersModal}
        onClose={() => {
          setShowNoWrongAnswersModal(false);
          setSelectedTeamForModal(null);
        }}
        onUseShowFirstLetter={handleUseShowFirstLetter}
        onUseShowAnswerPattern={handleUseShowAnswerPattern}
        teamName={selectedTeamForModal?.name}
        themeColors={{
          primary: "#29a6d6",
          primaryHover: "#1e7ba6",
          background: "#eaf6fb",
          border: "#bfe4f3",
        }}
      />

      {/* First Letter Hint - Absolute positioned */}
      {showFirstLetterHint && firstLetterHint && (
        <div className="fixed bottom-4 right-4 z-40">
          <div
            className={`bg-white rounded-xl shadow-2xl border-3 border-[#29a6d6] transition-all duration-300 ${
              isFirstLetterMinimized ? "w-auto" : "w-48"
            }`}
          >
            <div className="bg-[#29a6d6] p-2 rounded-t-lg flex justify-between items-center">
              <button
                onClick={() =>
                  setIsFirstLetterMinimized(!isFirstLetterMinimized)
                }
                className="text-white hover:bg-[#ffffff20] rounded-full p-1 transition-colors"
              >
                {isFirstLetterMinimized ? (
                  <FaChevronUp size={16} />
                ) : (
                  <FaChevronDown size={16} />
                )}
              </button>
              <h3
                className="text-white font-bold text-sm flex-1 text-center"
                style={{ direction: "rtl" }}
              >
                الحرف الأول
              </h3>
              <button
                onClick={() => setShowFirstLetterHint(false)}
                className="text-white hover:bg-[#ffffff20] rounded-full p-1 transition-colors"
              >
                <FaXmark size={16} />
              </button>
            </div>

            {!isFirstLetterMinimized && (
              <div className="p-4">
                <p
                  className="text-center text-[#29a6d6] text-xs mb-3"
                  style={{ direction: "rtl" }}
                >
                  الحرف الأول من الإجابة
                </p>
                <p
                  className="text-4xl font-bold text-gray-800 text-center"
                  style={{ direction: "rtl" }}
                >
                  {firstLetterHint}
                </p>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Answer Pattern Hint - Absolute positioned */}
      {showAnswerPattern && answerPatternInfo && (
        <div
          className="fixed bottom-4 right-4 z-40"
          style={{
            bottom: showFirstLetterHint ? "88px" : "16px",
          }}
        >
          <div
            className={`bg-white rounded-xl shadow-2xl border-3 border-[#29a6d6] transition-all duration-300 ${
              isAnswerPatternMinimized ? "w-auto" : "w-64"
            }`}
          >
            <div className="bg-[#29a6d6] p-2 rounded-t-lg flex justify-between items-center">
              <button
                onClick={() =>
                  setIsAnswerPatternMinimized(!isAnswerPatternMinimized)
                }
                className="text-white hover:bg-[#ffffff20] rounded-full p-1 transition-colors"
              >
                {isAnswerPatternMinimized ? (
                  <FaChevronUp size={16} />
                ) : (
                  <FaChevronDown size={16} />
                )}
              </button>
              <h3
                className="text-white font-bold text-sm flex-1 text-center"
                style={{ direction: "rtl" }}
              >
                نمط الإجابة
              </h3>
              <button
                onClick={() => setShowAnswerPattern(false)}
                className="text-white hover:bg-[#ffffff20] rounded-full p-1 transition-colors"
              >
                <FaXmark size={16} />
              </button>
            </div>

            {!isAnswerPatternMinimized && (
              <div className="p-4">
                <p
                  className="text-center text-[#29a6d6] text-xs mb-3"
                  style={{ direction: "rtl" }}
                >
                  نمط الكلمات والأحرف
                </p>
                <div className="text-center space-y-3">
                  <p
                    className="text-2xl font-bold text-gray-800"
                    style={{ direction: "rtl", letterSpacing: "0.1em" }}
                  >
                    {answerPatternInfo.pattern}
                  </p>
                  <p className="text-sm text-gray-600">
                    {answerPatternInfo.wordCount} كلمة •{" "}
                    {answerPatternInfo.charCount} حرف
                  </p>
                </div>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default MKHQuestion;
